import ConstructionIcon from "@mui/icons-material/Construction";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import Inventory2 from "@mui/icons-material/Inventory2";
import Launch from "@mui/icons-material/Launch";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import Tag from "@mui/icons-material/Tag";
import {
  alpha,
  Box,
  CardActionArea,
  Link,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { LoadingSpinner } from "@tngtech/tng-ui";
import { type PropsWithChildren, type ReactNode } from "react";
import { Link as RouterLink } from "react-router";
import { useTranslator } from "../../i18n/translator";
import { ErrorDisplay } from "../ErrorDisplay";
import { InfoToolTip } from "../InfoTooltip";
import { CollectionDeadline } from "./CollectionDeadline";
import { ContentParagraphs } from "./ContentText";
import "./frontpage.css";
import type { ContactEntry, FaqLink } from "./frontpageContent";
import { FrontpageLayout } from "./FrontpageLayout";
import { SantaFaceIcon } from "./SantaFaceIcon";
import { useFrontpageContent } from "./useConfidentialContent";

const quickCardSx: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 1.75,
  minWidth: 0,
  p: "18px 20px",
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3.5,
  color: "text.primary",
  textDecoration: "none",
  "&:hover": { borderColor: "primary.main" },
  "&.Mui-focusVisible": {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});
const QuickActionIcon = ({ children }: PropsWithChildren) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      display: "grid",
      placeItems: "center",
      width: 40,
      height: 40,
      borderRadius: 2.5,
      background: (theme) =>
        `linear-gradient(160deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      color: "primary.contrastText",
      "& svg": { width: 20, height: 20 },
    }}
  >
    {children}
  </Box>
);
const QuickActionTitle = ({ children }: PropsWithChildren) => (
  <Typography
    variant="inherit"
    component="span"
    sx={{
      fontWeight: 600,
      fontSize: 15,
      lineHeight: 1.35,
      overflowWrap: "break-word",
    }}
  >
    {children}
  </Typography>
);
const ExternalLinkIcon = () => (
  <Launch
    className="qc-external"
    aria-hidden="true"
    sx={{
      fontSize: 12,
      ml: 0.75,
      verticalAlign: "-2px",
      color: "text.secondary",
    }}
  />
);

const UnavailableQuickAction = ({
  icon,
  title,
  reason,
  reasonId,
  label,
}: {
  icon: ReactNode;
  title: string;
  reason: string;
  reasonId: string;
  label: string;
}) => (
  <Box
    className="quick-card"
    sx={[
      quickCardSx,
      {
        cursor: "default",
        color: "text.disabled",
        "&:hover": { borderColor: "divider" },
      },
    ]}
  >
    <Box
      component="span"
      sx={{
        display: "contents",
        "& > span": {
          background: "none",
          bgcolor: "action.disabledBackground",
          color: "text.disabled",
        },
      }}
    >
      <QuickActionIcon>{icon}</QuickActionIcon>
    </Box>
    <QuickActionTitle>
      {title}
      {}
      <Box
        component="span"
        sx={{
          display: "block",
          color: "text.primary",
          fontWeight: 400,
          "& .MuiSvgIcon-root": { ml: 0 },
        }}
      >
        <InfoToolTip
          helpertext={reason}
          ariaDescribedById={reasonId}
          icon={ConstructionIcon}
        />
        <Box
          component="span"
          sx={{ ml: 0.5, fontSize: "0.75rem", verticalAlign: "middle" }}
        >
          {label}
        </Box>
      </Box>
    </QuickActionTitle>
  </Box>
);

const QuickActions = ({ faq }: { faq: FaqLink }) => {
  const translator = useTranslator();
  return (
    <Box component="section" className="quick" sx={{ mt: 4.5 }}>
      <Box
        className="quick-cards"
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(230px, 100%), 1fr))",
          gap: 1.75,
        }}
      >
        <CardActionArea
          component={RouterLink}
          to="/guide"
          className="quick-card"
          sx={quickCardSx}
        >
          <QuickActionIcon>
            <HelpOutline />
          </QuickActionIcon>
          <QuickActionTitle>{translator("guide")}</QuickActionTitle>
        </CardActionArea>
        <CardActionArea
          component={RouterLink}
          to="/my-signed-cards"
          className="quick-card"
          sx={quickCardSx}
        >
          <QuickActionIcon>
            <Inventory2 />
          </QuickActionIcon>
          <QuickActionTitle>{translator("mySignedCards")}</QuickActionTitle>
        </CardActionArea>
        <UnavailableQuickAction
          icon={<SantaFaceIcon />}
          title={translator("myChristmasCardPortrait")}
          reason={translator("pageNotAvailable")}
          reasonId="christmasCardPortraitQuickActionNotYetAvailable"
          label={translator("comingSoon")}
        />
        <CardActionArea
          component="a"
          href={faq.href}
          target="_blank"
          rel="noopener noreferrer"
          className="quick-card"
          sx={quickCardSx}
        >
          <QuickActionIcon>
            <QuestionAnswerOutlined />
          </QuickActionIcon>
          <QuickActionTitle>
            {faq.label}
            <ExternalLinkIcon />
          </QuickActionTitle>
        </CardActionArea>
      </Box>
    </Box>
  );
};
const ContactCard = ({ contact }: { contact: ContactEntry }) => (
  <Paper
    component="li"
    variant="outlined"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      p: "16px 18px",
      bgcolor: "grey.50",
      borderRadius: 3,
    }}
  >
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        width: 42,
        height: 42,
        display: "grid",
        placeItems: "center",
        borderRadius: 2.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        color: "primary.main",
        "& svg": { fontSize: 20 },
      }}
    >
      {contact.kind === "slack" ? <Tag /> : <EmailOutlined />}
    </Box>
    <Stack sx={{ gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="inherit"
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {contact.label}
      </Typography>
      <Link
        href={contact.href}
        {...(contact.kind === "slack"
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        sx={{ fontSize: 15, fontWeight: 600, overflowWrap: "anywhere" }}
      >
        {contact.text}
      </Link>
    </Stack>
  </Paper>
);
export const LandingPage = () => {
  const { data, error } = useFrontpageContent();
  if (error)
    return (
      <Box p={2}>
        <ErrorDisplay error={error} />
      </Box>
    );
  if (!data) return <LoadingSpinner />;
  const { landing } = data;
  return (
    <FrontpageLayout>
      <Box component="section" className="hero">
        <Typography
          variant="inherit"
          component="h1"
          sx={{
            fontSize: "clamp(28px, 4vw, 46px)",
            fontWeight: 600,
            lineHeight: 1.15,
            maxWidth: "21em",
            textWrap: "balance",
          }}
        >
          {landing.title.text}
          <Box
            component="span"
            sx={{ color: "#b5dffd", boxDecorationBreak: "clone" }}
          >
            {landing.title.accent}
          </Box>
        </Typography>
        <Box
          className="hero-intro"
          sx={{
            mt: 2,
            maxWidth: "62em",
            fontSize: 16.5,
            lineHeight: 1.65,
            color: "rgba(255,255,255,0.92)",
            "& p+p": { mt: 1 },
          }}
        >
          <ContentParagraphs paragraphs={landing.intro} />
        </Box>
        <CollectionDeadline
          key={landing.deadline.date}
          content={landing.deadline}
        />
      </Box>
      <QuickActions faq={landing.faq} />
      {landing.sections.map((section) => (
        <Paper
          component="section"
          className="section-card"
          key={section.title}
          variant="outlined"
          sx={{
            mt: 3,
            p: 3.25,
            borderRadius: 3.5,
            "& p": { mt: 1.5, lineHeight: 1.65 },
          }}
        >
          <Typography
            variant="inherit"
            component="h2"
            sx={{ fontSize: 21, fontWeight: 600 }}
          >
            {section.title}
          </Typography>
          <ContentParagraphs paragraphs={section.paragraphs} />
          {section.contacts.length > 0 && (
            <Box
              component="ul"
              className="contacts"
              sx={{
                m: 0,
                p: 0,
                mt: "15px",
                listStyle: "none",
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
                gap: 1.5,
              }}
            >
              {section.contacts.map((contact) => (
                <ContactCard key={contact.href} contact={contact} />
              ))}
            </Box>
          )}
        </Paper>
      ))}
    </FrontpageLayout>
  );
};
